import { useAuthContext } from '../context/AuthContext'
import '../styles/landing.css'

/* Stroke icons (SF Symbols-style) — the design direction replaces emoji
   with tinted line icons; see docs for the "Quiet Premium" rationale. */
const ICONS = {
  bank: (
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <path d="M3 10h18M5 10v10M9.5 10v10M14.5 10v10M19 10v10M3 20h18M12 3l9 7H3z" />
    </svg>
  ),
  profile: (
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <path d="M8 4h8a2 2 0 0 1 2 2v14l-6-3-6 3V6a2 2 0 0 1 2-2z" />
    </svg>
  ),
  voice: (
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <path d="M12 3v18M7 7v10M17 7v10M3 10v4M21 10v4" />
    </svg>
  ),
  chat: (
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <path d="M21 12a8 8 0 0 1-8 8H5l-2 2V12a8 8 0 0 1 8-8h2a8 8 0 0 1 8 8z" />
    </svg>
  ),
  shield: (
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <path d="M12 3l8 4v5c0 5-3.5 8-8 9-4.5-1-8-4-8-9V7z" />
    </svg>
  ),
  lock: (
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <rect x="4" y="10" width="16" height="10" rx="2" />
      <path d="M8 10V7a4 4 0 0 1 8 0v3" />
    </svg>
  ),
  plus: (
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <path d="M12 5v14M5 12h14" />
    </svg>
  ),
}

const HOW_IT_WORKS = [
  {
    id: 'accounts',
    icon: ICONS.bank,
    title: 'Connect accounts',
    description: 'Link banks, cards, and investments securely with Plaid. Read-only, always.',
  },
  {
    id: 'profile',
    icon: ICONS.profile,
    title: 'Build your profile',
    description: "A short guided form fills the gaps your accounts can't: goals, family, timeline.",
  },
  {
    id: 'voice',
    icon: ICONS.voice,
    title: 'Talk it through',
    description: 'Walk through your finances out loud with live voice guidance — like a first meeting with a planner.',
  },
  {
    id: 'claude',
    icon: ICONS.chat,
    title: 'Ask Claude anything',
    description: 'Ongoing expert guidance grounded in your actual numbers, not generic advice.',
  },
]

const SECURITY_POINTS = [
  {
    id: 'readonly',
    icon: ICONS.shield,
    title: 'Read-only by design',
    description: 'Vault sees balances and transactions through Plaid. It cannot move, spend, or withdraw money.',
  },
  {
    id: 'encrypted',
    icon: ICONS.lock,
    title: 'Encrypted end to end',
    description: 'Data is encrypted in transit and at rest. Your credentials go to your bank — never to us.',
  },
  {
    id: 'deletable',
    icon: ICONS.plus,
    title: 'Yours to delete',
    description: 'Export or permanently delete your data at any time, from one place in settings.',
  },
]

/* Example figures only — this card is a product illustration, not user data. */
const EXAMPLE_ACCOUNTS = [
  { id: 'checking', label: 'Checking · Chase', balance: '$8,412.20' },
  { id: '401k', label: '401(k) · Fidelity', balance: '$182,506.44' },
  { id: 'brokerage', label: 'Brokerage · Schwab', balance: '$57,391.36' },
]

/**
 * Signed-out marketing landing page ("Quiet Premium" direction).
 * Rendered by HomePage when there is no authenticated session.
 *
 * @returns JSX.Element The landing page
 */
export default function LandingPage() {
  const { beginGoogleLogin } = useAuthContext()

  const handleSignIn = () => {
    void beginGoogleLogin(window.location.origin + '/accounts')
  }

  return (
    <div className="landing">
      <nav className="lp-nav" aria-label="Landing">
        <div className="lp-wrap lp-nav-in">
          <a className="lp-wordmark" href="/">
            <span className="lp-mark" aria-hidden="true" />
            Vault
          </a>
          <div className="lp-nav-links">
            <a href="#how-it-works">How it works</a>
            <a href="#security">Security</a>
          </div>
          <button type="button" className="lp-btn lp-btn--solid" onClick={handleSignIn}>
            Sign in
          </button>
        </div>
      </nav>

      <header className="lp-hero-section">
        <div className="lp-wrap lp-hero">
          <div>
            <p className="lp-kicker lp-rv">AI financial planning</p>
            <h1 className="lp-h1 lp-rv lp-d1">
              A financial plan that actually knows <span className="lp-grad">your finances.</span>
            </h1>
            <p className="lp-sub lp-rv lp-d2">
              Vault connects to your real accounts, builds a complete picture of your money,
              and gives you a plan you can talk to — by chat or live voice.
            </p>
            <div className="lp-hero-cta lp-rv lp-d3">
              <button type="button" className="lp-btn lp-btn--solid lp-btn--lg" onClick={handleSignIn}>
                Get started free
              </button>
              <a className="lp-btn lp-btn--lg" href="#how-it-works">
                See how it works
              </a>
            </div>
            <p className="lp-hero-note lp-rv lp-d4">
              Bank connections are <strong>read-only</strong> via Plaid. Vault never moves your money.
            </p>
          </div>

          <aside className="lp-plan-card lp-rv lp-d2" aria-label="Example plan overview">
            <div className="lp-pc-head">
              <span className="lp-pc-label">Plan progress</span>
              <span className="lp-pc-pill">On track</span>
            </div>
            <div>
              <span className="lp-pc-num">$248,310</span>
              <span className="lp-pc-delta">▲ 2.4% this month</span>
            </div>
            <div className="lp-bar">
              <span className="lp-bar-fill" />
            </div>
            <div className="lp-bar-meta">
              <span>Emergency fund · Retirement · Debt</span>
              <span>68%</span>
            </div>
            <div className="lp-pc-rows">
              {EXAMPLE_ACCOUNTS.map(account => (
                <div className="lp-pc-row" key={account.id}>
                  <span>{account.label}</span>
                  <span>{account.balance}</span>
                </div>
              ))}
            </div>
            <div className="lp-chat-snip">
              <b>Claude:</b> You're 4 months from a full emergency fund. Moving $250/mo from
              checking gets you there by November — want me to model it?
            </div>
          </aside>
        </div>
      </header>

      <section className="lp-section" id="how-it-works">
        <div className="lp-wrap">
          <p className="lp-sec-kicker">How it works</p>
          <h2 className="lp-h2">From connected accounts to a working plan in one sitting.</h2>
          <div className="lp-steps">
            {HOW_IT_WORKS.map((step, index) => (
              <div className="lp-step" key={step.id}>
                <span className="lp-step-n">{String(index + 1).padStart(2, '0')}</span>
                <div className="lp-step-icon">{step.icon}</div>
                <h3>{step.title}</h3>
                <p>{step.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="lp-section" id="security">
        <div className="lp-wrap">
          <p className="lp-sec-kicker">Security</p>
          <h2 className="lp-h2">Built like the accounts it connects to.</h2>
          <div className="lp-trust">
            {SECURITY_POINTS.map(point => (
              <div key={point.id}>
                <h3>
                  {point.icon}
                  {point.title}
                </h3>
                <p>{point.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="lp-section lp-cta">
        <div className="lp-wrap">
          <div>
            <h2 className="lp-h2">Fifteen minutes to a real plan.</h2>
            <p className="lp-sec-sub">Free to start. No card required.</p>
          </div>
          <button type="button" className="lp-btn lp-btn--solid lp-btn--lg" onClick={handleSignIn}>
            Get started free
          </button>
        </div>
      </section>
    </div>
  )
}
